import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { APP_ROUTES } from '../constants';
import { APP_CONFIG, convertMinuteToMiliseconds } from '../utility';
import { getSessionItemWithExpiry, setSessionItemWithExpiry } from '../utility/utility';
import { SESSION_STORAGE_KEY } from '../constant';
import { getCurrentLang } from './langHeader';
import i18n from '../i18n/i18n';

const baseUrl = import.meta.env.VITE_BASE_URL;

// Shared promise to prevent multiple simultaneous refresh requests
type RefreshPromiseResult = { newAccessToken: string };
let refreshPromise: Promise<RefreshPromiseResult> | null = null;

const instance = axios.create({
  baseURL: baseUrl,
  // Slightly above nginx's 300s proxy timeout (not equal to it) so nginx is
  // always the one that times out first on a genuinely slow-but-valid upload,
  // instead of racing the client's own timeout at the exact same instant.
  timeout: 360000,
  headers: { 'Content-Type': 'multipart/form-data' },
});
const responseBody = (response: AxiosResponse) => response;

export const requests = {
  get: (url: string, params?: Record<string, any>): Promise<AxiosResponse> =>
    instance.get(url, { params }).then(responseBody),
  post: (url: string, body: any): Promise<AxiosResponse> =>
    instance.post(url, body).then(responseBody),
  patch: (url: string, body: any): Promise<AxiosResponse> =>
    instance.patch(url, body).then(responseBody),
  put: (url: string, body: any): Promise<AxiosResponse> =>
    instance.put(url, body).then(responseBody),
  delete: (url: string): Promise<AxiosResponse> => instance.delete(url).then(responseBody),
};

instance.interceptors.request.use(
  (config: any) => {
    const token =
      getSessionItemWithExpiry(SESSION_STORAGE_KEY.ACCESS_TOKEN) ||
      getSessionItemWithExpiry(SESSION_STORAGE_KEY.PUBLIC_TOKEN);
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    if (config.headers) {
      config.headers['Accept-Language'] = getCurrentLang();
    }
    return config;
  },
  function (error: any) {
    // Do something with request error
    return Promise.reject(error);
  },
);

instance.interceptors.response.use(
  (response: any) => {
    if (response.data.code < 0) {
      return Promise.reject(response.data.result);
    }

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401) {
      if (originalRequest._retry) {
        const reason =
          (error.response?.data as { message: string })?.message ||
          'Your session has expired or is no longer valid. Please log in again.';
        window.location.replace(`/unauthorized?reason=${encodeURIComponent(reason)}`);

        return Promise.reject(new Error('Persistent authorization failure.'));
      }
      const token = getSessionItemWithExpiry(SESSION_STORAGE_KEY.ACCESS_TOKEN);
      const refreshToken = getSessionItemWithExpiry(SESSION_STORAGE_KEY.REFRESH_TOKEN);

      if (token && refreshToken) {
        originalRequest._retry = true;
        return await handleUnauthorized(originalRequest);
      }

      return Promise.reject(error.response?.data || error.message);
    }

    // HTTP 413 (Request Entity Too Large) is returned by nginx/backend with a
    // NON-JSON (HTML) body, so it must be detected by status, not by a parsed
    // message. Reject with a structured, detectable error so downstream thunks
    // can surface a friendly message instead of a raw HTML string.
    if (error.response?.status === 413) {
      return Promise.reject({
        status: 413,
        message: i18n.t('post.upload_too_large'),
      });
    }

    // No error.response means the request never got a reply — either the
    // client's own axios timeout fired (ECONNABORTED) or the connection was
    // dropped/reset by the network or proxy mid-upload. Without this branch
    // these fall through to `error.message` as a bare string (e.g. "timeout of
    // 300000ms exceeded"), which isn't a valid { status, message } shape and
    // breaks downstream thunks expecting one.
    if (!error.response) {
      const isTimeout = error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '');
      return Promise.reject({
        status: isTimeout ? 408 : 0,
        message: isTimeout
          ? 'The upload took too long and was cancelled. Please check your connection and try again.'
          : 'Network error during upload. Please check your connection and try again.',
      });
    }

    return Promise.reject(error.response?.data || error.message);
  },
);

async function handleUnauthorized(originalRequest: AxiosRequestConfig): Promise<AxiosResponse> {
  // Create refresh promise if not already in progress
  if (!refreshPromise) {
    refreshPromise = (async (): Promise<RefreshPromiseResult> => {
      const refreshToken = getSessionItemWithExpiry(SESSION_STORAGE_KEY.REFRESH_TOKEN);
      if (!refreshToken) {
        refreshPromise = null; // Clear before redirect
        window.location.replace(APP_ROUTES.UNAUTHORIZED);
        throw new Error('No refresh token available');
      }

      try {
        const response = await axios.post(
          `${baseUrl}/auth/refresh`,
          {},
          { headers: { Authorization: `Bearer ${refreshToken}` } },
        );
        const newAccessToken = response.headers['accesstoken'];
        const newRefreshToken = response.headers['refreshtoken'];

        setSessionItemWithExpiry(
          'accessToken',
          newAccessToken,
          convertMinuteToMiliseconds(Number(APP_CONFIG.ACCESS_TOKEN_EXPIRY)),
        );
        setSessionItemWithExpiry(
          'refreshToken',
          newRefreshToken,
          convertMinuteToMiliseconds(Number(APP_CONFIG.REFRESH_TOKEN_EXPIRY)),
        );
        setCoreApiAccessTokenMultipart(newAccessToken);

        // CRITICAL: Clear refreshPromise AFTER tokens are stored but BEFORE returning
        refreshPromise = null;

        return { newAccessToken };
      } catch (error) {
        console.error('[api-multipart] Token refresh failed:', error);
        refreshPromise = null; // Clear on failure
        window.location.replace(APP_ROUTES.UNAUTHORIZED);
        throw error;
      }
    })();
  }

  // Store reference to current promise before awaiting
  const currentRefreshPromise = refreshPromise;

  // Wait for the token refresh to complete - this BLOCKS until refresh finishes
  const { newAccessToken } = await currentRefreshPromise;

  // Update the original request with new token
  if (!originalRequest.headers) originalRequest.headers = {};
  originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;

  // CRITICAL: Return the awaited retry response directly
  return instance(originalRequest);
}

export function setCoreApiAccessTokenMultipart(token: string) {
  instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

export function setCoreApiXApiKey(token: string) {
  instance.defaults.headers.common['x-api-key'] = `${token}`;
}

export function removeCoreApiAccessToken() {
  delete axios.defaults.headers.common['Authorization'];
}
export default instance;
